import React, { type ReactNode } from "react";
import cx from "classnames";

import { useBetslipStore } from "@/features/betslip/stores/betslipStore";
import * as styles from "@/features/betslip/components/parts/StakeStrip.module.scss";

type CellVariant = "field" | "plain";

type StakeStripProps = {
  leftSlot: ReactNode;
  middleSlot: ReactNode;
  rightSlot: ReactNode;
  /** Caption shown above each cell value. Default: ["Stake", "Odds", "Payout"]. */
  labels?: [ReactNode, ReactNode, ReactNode];
  /** "field" renders the cell as a raised bordered box (stake-style cells);
   *  "plain" is the flat display look. Default: ["field", "plain", "plain"]. */
  cellVariants?: [CellVariant, CellVariant, CellVariant];
  /** Quick-stake chips row rendered below the strip while a stake input
   *  is focused. */
  quickStake?: ReactNode;
  /** Tints the stake cell with the promo colour when Bonus Cash is applied. */
  promoActive?: boolean;
  testId?: string;
};

// The × / = are drawn as paths rather than font glyphs: Plus Jakarta Sans
// renders both below the em-box centre, so text can't be dead-centred in
// the badge circle.
const separatorGlyphs = {
  multiply: (
    <svg viewBox="0 0 6 6" aria-hidden="true">
      <path d="M0.65 0.65 L5.35 5.35 M5.35 0.65 L0.65 5.35" />
    </svg>
  ),
  equals: (
    <svg viewBox="0 0 6 6" aria-hidden="true">
      <path d="M0.65 1.7 H5.35 M0.65 4.3 H5.35" />
    </svg>
  ),
};

const Separator = ({ glyph }: { glyph: keyof typeof separatorGlyphs }) => (
  <div className={styles.separator}>
    <span className={styles.separatorBadge}>{separatorGlyphs[glyph]}</span>
  </div>
);

const Cell = ({
  label,
  children,
  field,
  locked,
  promo,
  testId,
}: {
  label: ReactNode;
  children: ReactNode;
  field?: boolean;
  locked?: boolean;
  promo?: boolean;
  testId?: string;
}) => (
  <div
    className={cx(styles.cell, {
      [styles.cellField]: field,
      [styles.cellFieldLocked]: field && locked,
      [styles.cellPromo]: promo,
    })}
    data-testid={testId}
  >
    <div className={styles.inner}>
      <span className={styles.label}>{label}</span>
      <div className={styles.value}>{children}</div>
    </div>
  </div>
);

export const StakeStrip = ({
  leftSlot,
  middleSlot,
  rightSlot,
  labels = ["Stake", "Odds", "Payout"],
  cellVariants = ["field", "plain", "plain"],
  quickStake,
  promoActive = false,
  testId,
}: StakeStripProps) => {
  const isStakeLocked = useBetslipStore((s) => s.isStakeLocked);

  return (
    <>
      <div className={styles.strip} data-testid={testId}>
        <Cell
          label={labels[0]}
          field={cellVariants[0] === "field"}
          locked={isStakeLocked}
          promo={promoActive && cellVariants[0] === "field"}
          testId={testId ? `${testId}-stake` : undefined}
        >
          {leftSlot}
        </Cell>
        <Separator glyph="multiply" />
        <Cell
          label={labels[1]}
          field={cellVariants[1] === "field"}
          locked={isStakeLocked}
          promo={promoActive && cellVariants[1] === "field"}
          testId={testId ? `${testId}-odds` : undefined}
        >
          {middleSlot}
        </Cell>
        <Separator glyph="equals" />
        <Cell
          label={labels[2]}
          field={cellVariants[2] === "field"}
          locked={isStakeLocked}
          testId={testId ? `${testId}-payout` : undefined}
        >
          {rightSlot}
        </Cell>
      </div>
      {quickStake && <div className={styles.quickStake}>{quickStake}</div>}
    </>
  );
};
